/**
 * EmailThreadTags.jsx — Wave 1u3 Phase B / G4 (custom tags per thread)
 *
 * Renders inline chip-style tags + an "add tag" popover that suggests
 * existing org tags. Persists via PUT /api/mailbox/threads/{id}/tags.
 *
 * Props:
 *   - threadId: string (required)
 *   - tags: string[] (current tags from the thread detail payload)
 *   - onChange: (newTags: string[]) => void (called after save)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Tag, X, Plus, Loader2 } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const CHIP_COLOR_POOL = [
  ['#FEE2E2', '#991B1B', '#FCA5A5'], // red
  ['#FEF3C7', '#92400E', '#FDE68A'], // amber
  ['#DCFCE7', '#166534', '#86EFAC'], // green
  ['#DBEAFE', '#1E40AF', '#93C5FD'], // blue
  ['#FCE7F3', '#9D174D', '#F9A8D4'], // pink
  ['#E0E7FF', '#3730A3', '#A5B4FC'], // indigo
  ['#F3E8FF', '#6B21A8', '#D8B4FE'], // violet
  ['#CCFBF1', '#115E59', '#5EEAD4'], // teal
];

function hashTag(tag) {
  let h = 0;
  for (let i = 0; i < tag.length; i += 1) {
    h = (h * 31 + tag.charCodeAt(i)) | 0;
  }
  return CHIP_COLOR_POOL[Math.abs(h) % CHIP_COLOR_POOL.length];
}

export default function EmailThreadTags({ threadId, tags = [], onChange }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [orgTags, setOrgTags] = useState([]);
  const inputRef = useRef(null);

  // Lazy-load org tags when opening the editor
  useEffect(() => {
    if (!editing) return;
    let alive = true;
    api.get('/mailbox/tags')
      .then(({ data }) => { if (alive) setOrgTags(data?.tags || []); })
      .catch(() => { if (alive) setOrgTags([]); });
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => { alive = false; };
  }, [editing]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/mailbox/threads/${threadId}/tags`, { tags: next });
      onChange?.(data?.tags || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de mettre à jour les tags' });
    } finally {
      setSaving(false);
    }
  };

  const addTag = async (raw) => {
    const tag = (raw || input || '').trim().toLowerCase();
    if (!tag) return;
    if (tags.includes(tag)) {
      setInput('');
      return;
    }
    const next = [...tags, tag];
    setInput('');
    await save(next);
  };

  const removeTag = async (tag) => {
    const next = tags.filter((t) => t !== tag);
    await save(next);
  };

  const suggestions = (orgTags || [])
    .filter((t) => !tags.includes(t.name))
    .filter((t) => !input.trim() || t.name.includes(input.trim().toLowerCase()))
    .slice(0, 6);

  return (
    <div className="inline-flex items-center gap-1 flex-wrap" data-testid="email-thread-tags">
      {tags.map((t) => {
        const [bg, fg, br] = hashTag(t);
        return (
          <span
            key={t}
            data-testid={`email-tag-chip-${t}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold border"
            style={{ backgroundColor: bg, color: fg, borderColor: br }}
          >
            <Tag className="w-2.5 h-2.5" />
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="opacity-60 hover:opacity-100"
              title="Retirer ce tag"
              data-testid={`email-tag-remove-${t}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        );
      })}
      {editing ? (
        <div className="relative inline-flex items-center gap-1">
          <input
            ref={inputRef}
            data-testid="email-tag-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addTag(); }
              if (e.key === 'Escape') { setEditing(false); setInput(''); }
            }}
            placeholder="nouveau tag…"
            className="h-6 px-2 text-[11px] border border-[#C7D2FE] rounded bg-white focus:outline-none focus:border-[#4F46E5] w-32"
          />
          <button
            type="button"
            onClick={() => addTag()}
            disabled={saving || !input.trim()}
            data-testid="email-tag-confirm"
            className="h-6 px-1.5 rounded bg-[#4F46E5] text-white text-[10.5px] font-semibold inline-flex items-center disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Ajouter'}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setInput(''); }}
            className="h-6 w-6 rounded text-[#6B6C85] hover:bg-[#FAFAFE] inline-flex items-center justify-center"
            title="Annuler"
          >
            <X className="w-3 h-3" />
          </button>
          {suggestions.length > 0 && (
            <div
              data-testid="email-tag-suggestions"
              className="absolute top-full left-0 mt-1 z-50 bg-white border border-[#EFF0F6] rounded-lg shadow-lg p-1 min-w-[160px]"
            >
              {suggestions.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => addTag(s.name)}
                  data-testid={`email-tag-suggestion-${s.name}`}
                  className="w-full text-left px-2 py-1 rounded text-[11px] text-[#1A1B2E] hover:bg-[#EEF2FF] flex items-center justify-between"
                >
                  <span className="inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5 text-[#6B6C85]" />{s.name}</span>
                  <span className="text-[9.5px] text-[#8E8FA5]">{s.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="email-tag-add-btn"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold border border-dashed border-[#C7D2FE] text-[#4F46E5] hover:bg-[#EEF2FF]"
          title="Ajouter un tag personnalisé"
        >
          <Plus className="w-2.5 h-2.5" /> Tag
        </button>
      )}
    </div>
  );
}
